import { ALL_LANGUAGES, Language } from '@mi-front/core';
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material';
import { useIntl } from 'react-intl';

export type LanguageSelectAltProps = {
  readonly value?: string | null;
  readonly onChange: (languageId: string | null) => void;
  readonly onBlur?: (e: React.FocusEvent<any, Element>) => void;
  readonly allowed?: readonly string[];
  readonly disabled?: boolean;
  readonly error?: string | boolean;
  readonly autocompleteProps?: Omit<
    AutocompleteProps<Language, false, boolean, false>,
    | 'onChange'
    | 'value'
    | 'options'
    | 'getOptionLabel'
    | 'isOptionEqualToValue'
    | 'renderInput'
    | 'onBlur'
    | 'disabled'
    | 'disableClearable'
  >;
  readonly required?: boolean;
  readonly allowEmpty?: boolean;
};

export function LanguageSelectAlt(props: LanguageSelectAltProps) {
  const {
    value,
    onChange,
    onBlur,
    allowed,
    disabled,
    error,
    autocompleteProps,
    required,
    allowEmpty = false,
  } = props;
  const { formatMessage } = useIntl();

  function isAllowed(l: Language) {
    return !allowed || !allowed.length || allowed.includes(l.id);
  }

  const filteredLanguages = ALL_LANGUAGES.filter(isAllowed);

  const selectedLanguage = value ? filteredLanguages.find((l) => l.id === value) || null : null;

  const handleChange = (_event: React.SyntheticEvent, newValue: Language | null) => {
    if (newValue) {
      onChange(newValue.id);
    } else {
      onChange(null);
    }
  };

  return (
    <Autocomplete<Language, false, boolean, false>
      options={filteredLanguages}
      value={selectedLanguage}
      onChange={handleChange}
      onBlur={onBlur}
      disabled={disabled}
      autoHighlight
      disableClearable={!allowEmpty}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <TextField
          {...params}
          label={formatMessage({
            id: 'common.languageSelect.defaultLabel',
            defaultMessage: 'Idioma',
          })}
          error={!!error}
          helperText={typeof error === 'string' ? error : undefined}
          fullWidth
          required={required}
        />
      )}
      {...autocompleteProps}
    />
  );
}
